import React, { useState } from 'react';
import { Phone, MessageCircle, Instagram, MapPin, Calendar, User, Mail, ArrowRight } from 'lucide-react';
import { salonInfo, services } from '../data/mock';
import { toast } from 'sonner';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simulate form submission
    toast.success('Thank you! We will contact you shortly to confirm your booking.');
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: '',
      preferredDate: '',
      preferredTime: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWhatsAppBooking = () => {
    const message = `Hi! I would like to book an appointment at Posh Signature Beauty Salon.%0A%0AName: ${formData.name || '[Your Name]'}%0AService: ${formData.service || '[Service]'}%0APreferred Date: ${formData.preferredDate || '[Date]'}%0APreferred Time: ${formData.preferredTime || '[Time]'}`;
    window.open(`https://wa.me/${salonInfo.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <div className="pt-24 md:pt-28">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Book Your <span className="text-amber-600">Appointment</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose your preferred way to connect with us. We're here to make your booking experience seamless!
          </p>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* WhatsApp */}
            <a
              href={`https://wa.me/${salonInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-8 bg-green-50 rounded-2xl hover:bg-green-100 transition-all duration-300 group border-2 border-transparent hover:border-green-400"
            >
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 text-center">
                Quick & easy booking via WhatsApp chat
              </p>
            </a>

            {/* Phone Call */}
            <a
              href={`tel:${salonInfo.phone}`}
              className="flex flex-col items-center p-8 bg-blue-50 rounded-2xl hover:bg-blue-100 transition-all duration-300 group border-2 border-transparent hover:border-blue-400"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
              <p className="text-gray-600 text-center">
                Speak directly with our team
              </p>
              <p className="text-sm text-gray-500 mt-2">{salonInfo.phone}</p>
            </a>

            {/* Instagram */}
            <a
              href={salonInfo.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center p-8 bg-pink-50 rounded-2xl hover:bg-pink-100 transition-all duration-300 group border-2 border-transparent hover:border-pink-400"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Instagram DM</h3>
              <p className="text-gray-600 text-center">
                Message us on Instagram
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Or Fill This Quick Form
              </h2>
              <p className="text-gray-600">
                Share your details and we'll reach out to confirm your appointment
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                      placeholder="Your name"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              {/* Service */}
              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Service *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Choose a service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date */}
                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      id="preferredDate"
                      name="preferredDate"
                      required
                      value={formData.preferredDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Time */}
                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Time *
                  </label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    required
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select time</option>
                    <option value="10:00 AM">10:00 AM</option>
                    <option value="11:00 AM">11:00 AM</option>
                    <option value="12:00 PM">12:00 PM</option>
                    <option value="1:00 PM">1:00 PM</option>
                    <option value="2:00 PM">2:00 PM</option>
                    <option value="3:00 PM">3:00 PM</option>
                    <option value="4:00 PM">4:00 PM</option>
                    <option value="5:00 PM">5:00 PM</option>
                    <option value="6:00 PM">6:00 PM</option>
                    <option value="7:00 PM">7:00 PM</option>
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Special Requests (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all duration-200"
                  placeholder="Any special requests or preferences?"
                ></textarea>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Submit Request
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={handleWhatsAppBooking}
                  className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-green-500 text-white rounded-full hover:bg-green-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Book via WhatsApp
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Walk-In Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12 text-center">
            <MapPin className="w-12 h-12 text-amber-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Walk-Ins Welcome!
            </h2>
            <p className="text-xl text-gray-600 mb-6 max-w-2xl mx-auto">
              You can also visit us directly at our salon. However, we recommend booking in advance to avoid waiting times.
            </p>
            <div className="space-y-2 text-gray-700">
              <p className="font-semibold">{salonInfo.address}</p>
              <p><strong>Monday:</strong> <span className="text-amber-600 font-semibold">{salonInfo.hours.monday}</span></p>
              <p><strong>Tuesday - Thursday:</strong> {salonInfo.hours.tuesday}</p>
              <p><strong>Friday:</strong> {salonInfo.hours.friday}</p>
              <p><strong>Saturday - Sunday:</strong> {salonInfo.hours.saturday}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
